/* News Client barrel export */

import { INewsClient } from './INewsClient';
import { NewsClientApi } from './newsClientApi';
import { NewsClientMocked } from './newsClientMocked';
import { CreateUpdateNewsItemDto, CreateUpdateNewsTagDto } from './newsTypes';

// Re-export types
export * from './newsTypes';
export * from './INewsClient';

/* ---- Configuration ---- */

import { shouldUseMockedClient } from '../../environment';

/**
 * Determines whether to use mocked clients.
 * Automatically uses mocked clients on localhost and ofstest5 environments.
 */
const isMockedEnvironment = () => shouldUseMockedClient();

/* ---- Singleton Instance ---- */

/**
 * News client singleton instance.
 * Automatically uses mocked data on localhost and ofstest5 environments.
 * Import directly: `import { newsClient } from 'app/services/api/news';`
 */
export const newsClient: INewsClient = isMockedEnvironment()
  ? new NewsClientMocked()
  : new NewsClientApi();

/* ---- Factory Function (Backwards Compatibility) ---- */

/**
 * @deprecated Use `newsClient` directly instead.
 * Get the NewsClient instance.
 */
export function getNewsClient(): INewsClient {
  return newsClient;
}

/* ---- Backwards Compatibility ---- */

/**
 * @deprecated Use getNewsClient() instead
 */
export class NewsClient implements INewsClient {
  private client: INewsClient;

  constructor() {
    this.client = getNewsClient();
  }

  getNewsItems = (filters?: Parameters<INewsClient['getNewsItems']>[0]) => this.client.getNewsItems(filters);
  getNewsItem = (id: number) => this.client.getNewsItem(id);
  createNewsItem = (dto: CreateUpdateNewsItemDto) => this.client.createNewsItem(dto);
  updateNewsItem = (id: number, dto: CreateUpdateNewsItemDto) => this.client.updateNewsItem(id, dto);
  deleteNewsItem = (id: number) => this.client.deleteNewsItem(id);
  getComments = (newsItemId: number) => this.client.getComments(newsItemId);
  addComment = (newsItemId: number, content: string) => this.client.addComment(newsItemId, content);
  deleteComment = (commentId: number) => this.client.deleteComment(commentId);
  getEngagementSummary = (newsItemId: number) => this.client.getEngagementSummary(newsItemId);
  recordView = (newsItemId: number) => this.client.recordView(newsItemId);
  toggleLike = (newsItemId: number, liked: boolean) => this.client.toggleLike(newsItemId, liked);
  getTags = (filters?: Parameters<INewsClient['getTags']>[0]) => this.client.getTags(filters);
  getTag = (id: number) => this.client.getTag(id);
  createTag = (dto: CreateUpdateNewsTagDto) => this.client.createTag(dto);
  updateTag = (id: number, dto: CreateUpdateNewsTagDto) => this.client.updateTag(id, dto);
  deleteTag = (id: number) => this.client.deleteTag(id);
}
